import { useApolloClient, useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { QR_LIST_FAVORITE } from "../graphQl/FavoritesAPI";
import { QR_LIST_TRIP_PUBLICATION_SUMMARY } from "../../graphQl/TripsAPI";
import { QR_GET_TRIP_PUBLICATION_SUMMARY } from "../../../tripDetails/detailsTrip/graphQl/DetailsTripAPI";

/**
 * Hook combiné pour récupérer :
 * 1️⃣ la liste des favoris
 * 2️⃣ les publications correspondantes aux favoris
 */
export const useFavoritePublications = (
  personId,
  serviceType,
  page = 0,
  size = 6,
  filters = {}
) => {
  const client = useApolloClient();
  const [fallbackPublications, setFallbackPublications] = useState([]);
  const [fallbackLoading, setFallbackLoading] = useState(false);
  const [fallbackError, setFallbackError] = useState(null);
  const lastFallbackSignatureRef = useRef("");

  // --- 1️⃣ Récupération favoris ---
  const {
    data: favData,
    loading: loadingFav,
    error: errorFav,
    refetch: refetchFav,
  } = useQuery(QR_LIST_FAVORITE, {
    variables: {
      paginationParams: { currentPage: page, sizePage: size },
      personId,
      serviceType: { type: serviceType },   // ✅ FIX IMPORTANT
    },
    fetchPolicy: "cache-and-network",
  });

  const favorites = favData?.QR_LIST_FAVORITE?.data?.content || [];
  const totalPages = favData?.QR_LIST_FAVORITE?.data?.totalPages || 1;
  const currentPage = favData?.QR_LIST_FAVORITE?.data?.currentPage || 0;

  // --- 2️⃣ Extraction des entityId ---
  const publicationIds = useMemo(
    () => favorites.map((fav) => fav.entityId).filter(Boolean),
    [favorites]
  );

  // --- 3️⃣ Récupération publications correspondantes ---
  const {
    data: pubData,
    loading: loadingPub,
    error: errorPub,
    refetch: refetchPub,
  } = useQuery(QR_LIST_TRIP_PUBLICATION_SUMMARY, {
    skip: publicationIds.length === 0, // skip si aucun favori
    variables: {
      paginationParams: { currentPage: 0, sizePage: size },
      publicationIds,
      establishmentIds: filters.establishmentIds,
      destination: filters.destination,
      startDate: filters.startDate,
      endDate: filters.endDate,
      adultTravelers: filters.adultTravelers,
      childTravelers: filters.childTravelers,
      babiesTravelers: filters.babiesTravelers,
      maxPrice: filters.maxPrice,
      minPrice: filters.minPrice,
    },
    fetchPolicy: "cache-and-network",
  });

  const publications =
    pubData?.QR_LIST_TRIP_PUBLICATION_SUMMARY?.data?.content || [];
  const totalPubPages =
    pubData?.QR_LIST_TRIP_PUBLICATION_SUMMARY?.data?.totalPages || 1;

  const favoriteMap = useMemo(
    () =>
      new Map(
        favorites.map((favorite) => [
          String(favorite.entityId),
          {
            favoriteId: favorite.id,
            isFavorite: true,
          },
        ])
      ),
    [favorites]
  );

  const missingPublicationIds = useMemo(() => {
    const resolvedIds = new Set(
      publications.map((publication) => String(publication?.id)).filter(Boolean)
    );

    return publicationIds.filter(
      (publicationId) => !resolvedIds.has(String(publicationId))
    );
  }, [publicationIds, publications]);

  const fallbackSignature = useMemo(
    () => missingPublicationIds.map(String).sort().join(","),
    [missingPublicationIds]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchMissingPublications = async () => {
      if (loadingFav || loadingPub || publicationIds.length === 0) {
        return;
      }

      if (!fallbackSignature) {
        lastFallbackSignatureRef.current = "";
        if (isMounted) {
          setFallbackPublications([]);
          setFallbackError(null);
          setFallbackLoading(false);
        }
        return;
      }

      if (lastFallbackSignatureRef.current === fallbackSignature) {
        return;
      }

      if (isMounted) {
        setFallbackLoading(true);
        setFallbackError(null);
      }

      try {
        const results = await Promise.all(
          missingPublicationIds.map(async (tripPublicationId) => {
            const response = await client.query({
              query: QR_GET_TRIP_PUBLICATION_SUMMARY,
              variables: { tripPublicationId },
              fetchPolicy: "cache-first",
            });

            const publication =
              response?.data?.QR_GET_TRIP_PUBLICATION_SUMMARY?.data || null;
            if (!publication) return null;

            return {
              ...publication,
              ...(favoriteMap.get(String(publication.id)) || {
                favoriteId: null,
                isFavorite: true,
              }),
            };
          })
        );

        if (isMounted) {
          lastFallbackSignatureRef.current = fallbackSignature;
          setFallbackPublications(results.filter(Boolean));
        }
      } catch (error) {
        if (isMounted) {
          setFallbackError(error);
          setFallbackPublications([]);
        }
      } finally {
        if (isMounted) {
          setFallbackLoading(false);
        }
      }
    };

    fetchMissingPublications();

    return () => {
      isMounted = false;
    };
  }, [
    client,
    favoriteMap,
    fallbackSignature,
    loadingFav,
    loadingPub,
    missingPublicationIds,
    publicationIds.length,
  ]);

  const mergedPublications = useMemo(() => {
    const basePublications = publications.map((publication) => ({
      ...publication,
      ...(favoriteMap.get(String(publication.id)) || {}),
    }));

    const mergedMap = new Map(
      basePublications.map((publication) => [String(publication.id), publication])
    );

    fallbackPublications.forEach((publication) => {
      mergedMap.set(String(publication.id), publication);
    });

    return Array.from(mergedMap.values());
  }, [publications, fallbackPublications, favoriteMap]);

  return {
    favorites,
    publications: mergedPublications,
    totalPages,
    totalPubPages: mergedPublications.length > 0 ? 1 : totalPubPages,
    currentPage,
    loading: loadingFav || loadingPub || fallbackLoading,
    error: errorFav || errorPub || fallbackError,
    refetch: async () => {
      lastFallbackSignatureRef.current = "";
      await refetchFav();
      await refetchPub();
    },
  };
};
